import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, ShieldCheck, AlertTriangle, RefreshCw, ImageOff, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import EmptyState from "../components/EmptyState";
import { api, getToken } from "../api/client";
import { useAuth } from "../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status }) => {
    const map = {
        processing: { label: "Processing", cls: "bg-amber-50 text-amber-700 border-amber-200", icon: <RefreshCw className="h-3 w-3 animate-spin" /> },
        extracted: { label: "Extracted", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: <ShieldCheck className="h-3 w-3" /> },
        failed: { label: "Failed", cls: "bg-red-50 text-red-700 border-red-200", icon: <AlertTriangle className="h-3 w-3" /> },
    };
    const s = map[status] || map.processing;
    return (
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${s.cls}`}>
            {s.icon} {s.label}
        </span>
    );
};

const VerifyBadge = ({ status }) => {
    const map = {
        pending_review: { label: "Pending review", cls: "bg-zinc-100 text-zinc-700 border-zinc-200" },
        approved: { label: "Approved", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
        rejected: { label: "Rejected", cls: "bg-red-50 text-red-700 border-red-200" },
    };
    const s = map[status] || map.pending_review;
    return (
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${s.cls}`}>
            {s.label}
        </span>
    );
};

const AuthedImage = ({ id, alt = "" }) => {
    const [src, setSrc] = useState(null);
    const [err, setErr] = useState(false);

    useEffect(() => {
        let active = true;
        let blobUrl = null;
        const load = async () => {
            try {
                const r = await fetch(`${BACKEND_URL}/api/prescriptions/${id}/image`, {
                    headers: { Authorization: `Bearer ${getToken()}` },
                });
                if (!r.ok) throw new Error("fail");
                const blob = await r.blob();
                blobUrl = URL.createObjectURL(blob);
                if (active) setSrc(blobUrl);
            } catch {
                if (active) setErr(true);
            }
        };
        load();
        return () => {
            active = false;
            if (blobUrl) URL.revokeObjectURL(blobUrl);
        };
    }, [id]);

    if (err) return <div className="flex h-full w-full items-center justify-center bg-zinc-100 text-zinc-400"><ImageOff className="h-6 w-6" /></div>;
    if (!src) return <div className="h-full w-full skeleton" />;
    return <img src={src} alt={alt} className="h-full w-full object-cover" />;
};

const PrescriptionsContent = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const load = async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/prescriptions");
            setItems(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    // Poll while any prescription is still processing
    useEffect(() => {
        const hasProcessing = items.some((p) => p.ocr_status === "processing");
        if (!hasProcessing) return;
        const t = setInterval(() => { load(); }, 3000);
        return () => clearInterval(t);
    }, [items]);

    const onDelete = async (id) => {
        try {
            await api.delete(`/prescriptions/${id}`);
            toast.success("Prescription removed");
            setItems((arr) => arr.filter((p) => p.id !== id));
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : detail?.message || "Delete failed");
        }
    };

    if (loading) {
        return (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="rx-loading">
                {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-48 skeleton rounded-2xl" />)}
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <EmptyState
                title="No prescriptions yet"
                description="Upload a prescription image and let our AI extract the details for you."
                action={
                    <button onClick={() => navigate("/prescriptions/upload")} className="btn-brand" data-testid="empty-upload-btn">
                        <Plus className="h-4 w-4" /> Upload prescription
                    </button>
                }
            />
        );
    }

    return (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="rx-list">
            {items.map((p) => (
                <div
                    key={p.id}
                    className="group relative overflow-hidden rounded-2xl border border-zinc-100 bg-white transition-all hover:-translate-y-1 hover:border-brand-200 hover:shadow-brand"
                    data-testid={`rx-card-${p.id}`}
                >
                    <Link to={`/prescriptions/${p.id}`} className="block">
                        <div className="relative aspect-[4/3] overflow-hidden bg-zinc-50">
                            <AuthedImage id={p.id} alt="Prescription thumbnail" />
                            <div className="absolute left-3 top-3 flex flex-col gap-1">
                                <StatusBadge status={p.ocr_status} />
                                <VerifyBadge status={p.verification_status} />
                            </div>
                        </div>
                        <div className="p-4">
                            <p className="text-sm font-medium text-zinc-900">{p.doctor_name || "Doctor —"}</p>
                            <p className="text-xs text-zinc-500">{p.patient_name || "Patient —"}</p>
                            <div className="mt-3 flex items-center justify-between text-xs text-zinc-500">
                                <span>{p.prescription_date || new Date(p.uploaded_at).toLocaleDateString()}</span>
                                <span>{p.medicines_count} medicine{p.medicines_count === 1 ? "" : "s"}</span>
                            </div>
                        </div>
                    </Link>
                    <button
                        onClick={() => onDelete(p.id)}
                        className="absolute right-3 top-3 rounded-full bg-white/90 p-2 text-zinc-500 opacity-0 transition hover:text-red-500 group-hover:opacity-100"
                        title="Delete"
                        data-testid={`rx-delete-${p.id}`}
                    >
                        <Trash2 className="h-4 w-4" />
                    </button>
                </div>
            ))}
        </div>
    );
};

const Prescriptions = () => (
    <Layout>
        <section className="mx-auto max-w-7xl px-6 py-10 lg:px-12">
            <header className="mb-8 flex items-end justify-between gap-4">
                <div>
                    <p className="label-uppercase">Health records</p>
                    <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">My prescriptions</h1>
                    <p className="mt-2 text-sm text-zinc-500">Upload a prescription image — our AI extracts the structured data for use at checkout.</p>
                </div>
                <Link to="/prescriptions/upload" className="btn-brand !py-2 !px-5 !text-sm" data-testid="upload-new-btn">
                    <Plus className="h-4 w-4" /> Upload new
                </Link>
            </header>
            <AuthGuard>
                <PrescriptionsContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default Prescriptions;
